
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Pencil } from "lucide-react";
import { Facility } from "@/hooks/useFacilities";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";
import { VideoUpload } from "@/components/VideoUpload";

const facilitySchema = z.object({
  name: z.string().min(1, "Facility name is required"),
  description: z.string().optional(),
  facility_type: z.string().min(1, "Facility type is required"),
  image_url: z.string().optional(),
  video_url: z.string().optional(),
});

type FacilityFormData = z.infer<typeof facilitySchema>;

interface EditFacilityDialogProps {
  facility: Facility;
  onFacilityUpdated: () => void;
}

export const EditFacilityDialog = ({ facility, onFacilityUpdated }: EditFacilityDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<FacilityFormData>({
    resolver: zodResolver(facilitySchema),
    defaultValues: {
      name: "",
      description: "",
      facility_type: "",
      image_url: "",
      video_url: "",
    },
  });

  // Reset form when facility changes or dialog opens
  useEffect(() => {
    if (facility && open) {
      console.log("Resetting form with facility data:", facility);
      console.log("Facility type being set:", facility.facility_type);
      form.reset({
        name: facility.name || "",
        description: facility.description || "",
        facility_type: facility.facility_type || "",
        image_url: facility.image_url || "",
        video_url: facility.video_url || "",
      });
    }
  }, [facility, open, form]);

  const onSubmit = async (data: FacilityFormData) => {
    setIsLoading(true);
    console.log("Updating facility with data:", data);

    try {
      const facilityData = {
        name: data.name,
        description: data.description || null,
        facility_type: data.facility_type,
        image_url: data.image_url || null,
        video_url: data.video_url || null,
      };

      console.log("Updating facility data:", facilityData);

      const { data: updatedFacility, error } = await supabase
        .from('facilities')
        .update(facilityData)
        .eq('id', facility.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating facility:", error);
        throw error;
      }

      console.log("Facility updated successfully:", updatedFacility);

      toast({
        title: "Success",
        description: "Facility updated successfully!",
      });

      setOpen(false);
      onFacilityUpdated();

    } catch (error: any) {
      console.error("Error updating facility:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update facility",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUploaded = (url: string) => {
    form.setValue("image_url", url);
  };

  const handleVideoUploaded = (url: string) => {
    form.setValue("video_url", url);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Pencil className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Facility</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Infrared Sauna" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="facility_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Type *</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      console.log("Select value changed to:", value);
                      field.onChange(value);
                    }} 
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select facility type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Sauna">Sauna</SelectItem>
                      <SelectItem value="Ice Bath">Ice Bath</SelectItem>
                      <SelectItem value="Cryotherapy">Cryotherapy</SelectItem>
                      <SelectItem value="Fitness Equipment">Fitness Equipment</SelectItem>
                      <SelectItem value="Recovery Room">Recovery Room</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe your facility..." 
                      {...field} 
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Image</FormLabel>
                  <FormControl>
                    <ImageUpload
                      onImageUploaded={handleImageUploaded}
                      currentImageUrl={field.value}
                      bucketName="facility-images"
                      folder="facilities"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="video_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Video</FormLabel>
                  <FormControl>
                    <VideoUpload
                      onVideoUploaded={handleVideoUploaded}
                      currentVideoUrl={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Facility"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
